import { motion } from 'framer-motion';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const values = [
  {
    title: 'Family-first',
    description: 'Built for kids, parents, and grandparents—designed around real life, not gadgets.',
  },
  {
    title: 'Preventative over reactive',
    description: 'Surface meaningful changes early, so families can act before problems grow.',
  },
  {
    title: 'Screenless by intention',
    description: 'No distractions. Just quiet support that keeps you present.',
  },
  {
    title: 'Privacy-first design',
    description: 'Sharing is always controlled, so families stay connected without losing control.',
  },
  {
    title: 'Clarity over noise',
    description: 'Fewer metrics. Better insights. Only what truly matters.',
  },
];

const AboutPage = () => {
  const scrollToPlans = () => {
    window.location.href = '/#plans';
  };

  return (
    <div className="min-h-screen" data-testid="about-page">
      <Navigation />

      <main className="pt-24 pb-8">
        {/* Premium Belief Opener */}
        <section className="py-16 md:py-24">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] font-semibold text-[#1A1A1A] leading-[1.15] mb-6">
                We believe caring for family should be effortless.
              </h1>
              <p className="text-lg md:text-xl text-[#5A5A5A] leading-relaxed max-w-2xl mx-auto mb-10">
                We're building the world's first screenless wearable ecosystem for families—bringing wellness, safety, and connection into one app.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={scrollToPlans}
                  className="bg-[#1A1A1A] text-white px-8 py-3.5 rounded-full text-base font-medium
                             hover:bg-[#333] transition-all duration-300"
                >
                  Choose Your Plan
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsModalOpen(true)}
                  className="bg-white border border-black/10 text-[#1A1A1A] px-8 py-3.5 
                             rounded-full text-base hover:bg-black/5 transition-all duration-300"
                >
                  Reserve Yours
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-12 md:py-16 section-warm">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-10"
            >
              <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A]">
                The principles behind Connect
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`bg-white rounded-2xl border border-black/6 shadow-sm p-6 md:p-8
                             ${index === 4 ? 'md:col-span-2 md:max-w-md md:mx-auto' : ''}`}
                >
                  <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">
                    {value.title}
                  </h3>
                  <p className="text-[#5A5A5A] leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 4) Mission Card */}
        <section className="py-12 md:py-16">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto"
            >
              <div className="bg-gradient-to-br from-[#F5F0EA] to-[#EDE8E2] rounded-3xl border border-black/6 shadow-md p-10 md:p-14 text-center">
                <h2 className="text-sm uppercase tracking-[0.2em] text-[#7A7A7A] mb-6">
                  Our mission
                </h2>
                <p className="text-2xl md:text-3xl text-[#1A1A1A] leading-relaxed font-light">
                  "To make family wellness and safety effortless—so people can care earlier, live better, and worry less."
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 5) Designed for presence */}
        <section className="py-12 md:py-16">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl mx-auto"
            >
              <div className="bg-white rounded-2xl border border-black/6 shadow-sm p-8 md:p-10 text-center">
                <h3 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-3">
                  Designed for presence
                </h3>
                <p className="text-[#5A5A5A] leading-relaxed">
                  Connect is screenless by intention—so families spend less time checking devices, and more time living together.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 6) Bottom CTA Card */}
        <section className="py-12 md:py-16 section-warm">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto"
            >
              <div className="bg-white rounded-3xl border border-black/6 shadow-lg p-8 md:p-12 text-center">
                <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-3">
                  Join early access
                </h2>
                <p className="text-[#5A5A5A] mb-8">
                  Get launch updates, early access, and priority delivery windows.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setIsModalOpen(true)}
                    className="bg-[#1A1A1A] text-white px-8 py-3.5 rounded-full text-base font-medium
                               hover:bg-[#333] transition-all duration-300"
                  >
                    Reserve Yours
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={scrollToPlans}
                    className="bg-white border border-black/10 text-[#1A1A1A] px-8 py-3.5 
                               rounded-full text-base hover:bg-black/5 transition-all duration-300"
                  >
                    See Plans
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 7) Inspired by Family Section */}
        <section className="py-12 md:py-16">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto"
            >
              <div className="bg-gradient-to-br from-white to-[#FAF7F4] rounded-3xl border border-black/6 shadow-lg p-8 md:p-12">
                <h2 className="text-sm uppercase tracking-[0.2em] text-[#7A7A7A] mb-8 text-center">
                  Inspired by Family
                </h2>
                
                <div className="space-y-6 text-[#4A4A4A] leading-relaxed">
                  <p className="text-lg md:text-xl">
                    Swetha Reddy and Aswin Reddy — a couple deeply rooted in family values — are the kind of people who always think about everyone else first. As parents, they care about their children's safety, fitness, and daily routines. As a son and daughter, they also worry about their parents' health as they grow older.
                  </p>
                  
                  <p className="text-lg md:text-xl">
                    Like many families, they balance responsibility across generations — wanting reassurance without constant calls, clarity without intrusion, and connection without distraction.
                  </p>
                  
                  <p className="text-xl md:text-2xl font-medium text-[#1A1A1A] text-center py-4">
                    Connect was built for people like them.
                  </p>
                  
                  <div className="space-y-3 text-base md:text-lg text-[#5A5A5A] italic pl-4 border-l-2 border-[#E5E0DA]">
                    <p>For parents who want to know their children are safe.</p>
                    <p>For families who want visibility into wellness.</p>
                    <p>For sons and daughters who want to care from anywhere.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 8) Founder Section - LAST */}
        <section className="py-12 md:py-16">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl mx-auto"
            >
              <div className="bg-gradient-to-br from-white to-[#FAF7F4] rounded-3xl border border-black/6 shadow-md p-8 md:p-10 text-center">
                <span className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A] mb-4 block">
                  Founder
                </span>
                <h3 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-1">
                  Bharath Reddy
                </h3>
                <p className="text-[#6A6A6A] mb-6">
                  Founder, Connect
                </p>
                <p className="text-[#4A4A4A] leading-relaxed italic">
                  "Building the first family-first wearable ecosystem—designed to keep kids safer, parents supported, and families connected."
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
